import { Button } from "@roberttamas/ui";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { FontSchemeEditor } from "@/components/font-scheme-editor";
import { EditDialog, SettingRow } from "@/components/setting-row";
import { updateSiteConfig } from "@/lib/api";
import { toAppError } from "@/lib/errors";
import { DEFAULT_FONT_SCHEME } from "@/lib/fonts";
import { CUSTOM_TEMPLATE_ID } from "@/lib/templates";
import type { ConfigDoc, FontScheme } from "@/lib/types";

interface FontSchemeSectionProps {
  siteId: string;
  fontScheme?: ConfigDoc["fontScheme"];
}

export function FontSchemeSection({ siteId, fontScheme }: FontSchemeSectionProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const invalidate = useCallback(async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ["site", siteId] }),
      queryClient.invalidateQueries({ queryKey: ["sites"] }),
    ]);
  }, [queryClient, siteId]);

  const current: FontScheme = fontScheme ?? DEFAULT_FONT_SCHEME;
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<FontScheme>(current);

  const openDialog = () => {
    setDraft(fontScheme ?? DEFAULT_FONT_SCHEME);
    setOpen(true);
  };

  const mutation = useMutation({
    mutationFn: (scheme: FontScheme) =>
      updateSiteConfig({
        siteId,
        configPatch: { fontScheme: scheme, templateId: CUSTOM_TEMPLATE_ID },
      }),
    onSuccess: async () => {
      toast.success(t("fontSection.updated"));
      await invalidate();
      setOpen(false);
    },
    onError: (err) => toast.error(toAppError(err).message),
  });

  return (
    <>
      <SettingRow
        title={t("fontSection.title")}
        description={t("fontSection.desc")}
        action={
          <Button type="button" variant="ghost" size="sm" onClick={openDialog}>
            {t("common.edit")}
          </Button>
        }
        value={
          <div className="space-y-0.5 text-sm">
            <div>
              <span className="text-muted-foreground">{t("fontSection.heading")}</span> {current.heading.family}
            </div>
            <div>
              <span className="text-muted-foreground">{t("fontSection.body")}</span> {current.body.family}
            </div>
          </div>
        }
      />
      <EditDialog
        open={open}
        onOpenChange={setOpen}
        title={t("fontSection.editTitle")}
        description={t("fontSection.editDesc")}
        onSave={() => mutation.mutate(draft)}
        saving={mutation.isPending}
        contentClassName="sm:max-w-2xl"
      >
        <FontSchemeEditor value={draft} onChange={setDraft} />
      </EditDialog>
    </>
  );
}
